import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  message,
  Card,
  Input,
  Select,
  Row,
  Col,
  Statistic,
  Typography,
  Dropdown,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
  MoreOutlined,
  CheckOutlined,
  CloseOutlined,
  FilterOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { voucherService, VoucherQueryParams } from '../services/voucherService';
import { Voucher, VouchersApiResponse, VoucherFormData, VoucherType, VoucherStatus } from '../types';
import VoucherForm from '../components/vouchers/VoucherForm';
import VoucherDetail from '../components/vouchers/VoucherDetail';
import ErrorBoundary from '../components/ErrorBoundary';

const { Option } = Select;
const { Text } = Typography;

const VouchersPage: React.FC = () => {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isDetailVisible, setIsDetailVisible] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [voucherToDelete, setVoucherToDelete] = useState<Voucher | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState<VoucherQueryParams>({
    search: '',
    status: undefined,
    voucher_type: undefined,
    sortBy: 'created_at',
    sortOrder: 'desc'
  });
  const [summary, setSummary] = useState({
    totalAvailable: 0,
    totalClaimed: 0,
    totalUsed: 0,
    totalExpired: 0,
    totalValue: 0
  });

  // Fetch vouchers data
  const fetchVouchers = useCallback(async (params?: VoucherQueryParams) => {
    try {
      setLoading(true);
      const queryParams = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters,
        ...params
      };

      const response: VouchersApiResponse = await voucherService.getVouchers(queryParams);
      
      setVouchers(response.data.vouchers);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.totalVouchers,
        current: response.data.pagination.currentPage,
      }));
      setSummary(response.data.summary);
      
    } catch (error) {
      console.error('Error fetching vouchers:', error);
      message.error('Không thể tải danh sách voucher');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, filters]);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    try {
      await voucherService.getVoucherStatistics();
      // Statistics can be used to enhance the UI later
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  }, []);

  useEffect(() => {
    fetchVouchers();
    fetchStatistics();
  }, []);

  // Handle search
  const handleSearch = (value: string) => {
    setFilters((prev: VoucherQueryParams) => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle filter change
  const handleFilterChange = (key: keyof VoucherQueryParams, value: any) => {
    setFilters((prev: VoucherQueryParams) => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Apply filters
  const applyFilters = () => {
    fetchVouchers();
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      search: '',
      status: undefined,
      voucher_type: undefined,
      sortBy: 'created_at',
      sortOrder: 'desc'
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle table change (pagination, sorting)
  const handleTableChange = (paginationConfig: any, _filters: any, sorter: any) => {
    const newPagination = {
      ...pagination,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    };
    setPagination(newPagination);

    if (sorter.field) {
      setFilters((prev: VoucherQueryParams) => ({
        ...prev,
        sortBy: sorter.field,
        sortOrder: sorter.order === 'ascend' ? 'asc' : 'desc'
      }));
    }
  };

  // Handle create voucher
  const handleCreateVoucher = () => {
    setSelectedVoucher(null);
    setModalMode('create');
    setIsModalVisible(true);
  };

  // Handle edit voucher
  const handleEditVoucher = (voucher: Voucher) => {
    setSelectedVoucher(voucher);
    setModalMode('edit');
    setIsModalVisible(true);
  };

  // Handle view voucher detail
  const handleViewVoucher = (voucher: Voucher) => {
    setSelectedVoucher(voucher);
    setIsDetailVisible(true);
  };

  // Handle delete voucher
  const handleDeleteVoucher = (voucher: Voucher) => {
    setVoucherToDelete(voucher);
    setDeleteModalVisible(true);
  };

  // Confirm delete voucher
  const confirmDeleteVoucher = async () => {
    if (!voucherToDelete) return;

    try {
      await voucherService.deleteVoucher(voucherToDelete.id);
      message.success('Xóa voucher thành công');
      setDeleteModalVisible(false);
      setVoucherToDelete(null);
      fetchVouchers();
      fetchStatistics();
    } catch (error) {
      console.error('Error deleting voucher:', error);
      message.error('Không thể xóa voucher');
    }
  };

  // Handle form submit
  const handleFormSubmit = async (values: VoucherFormData) => {
    try {
      if (modalMode === 'create') {
        await voucherService.createVoucher(values);
        message.success('Tạo voucher thành công');
      } else if (selectedVoucher) {
        await voucherService.updateVoucher(selectedVoucher.id, values);
        message.success('Cập nhật voucher thành công');
      }
      
      setIsModalVisible(false);
      setSelectedVoucher(null);
      fetchVouchers();
      fetchStatistics();
    } catch (error) {
      console.error('Error saving voucher:', error);
      message.error(modalMode === 'create' ? 'Không thể tạo voucher' : 'Không thể cập nhật voucher');
    }
  };

  // Handle status update
  const handleStatusUpdate = async (voucherId: string, status: VoucherStatus) => {
    try {
      await voucherService.updateVoucherStatus(voucherId, status);
      message.success('Cập nhật trạng thái thành công');
      fetchVouchers();
      fetchStatistics();
    } catch (error) {
      console.error('Error updating status:', error);
      message.error('Không thể cập nhật trạng thái');
    }
  };

  // Handle bulk update
  const handleBulkUpdate = async (updates: any) => {
    if (selectedRowKeys.length === 0) {
      message.warning('Vui lòng chọn ít nhất một voucher');
      return;
    }

    try {
      await voucherService.bulkUpdateVouchers(selectedRowKeys as string[], updates);
      message.success(`Cập nhật ${selectedRowKeys.length} voucher thành công`);
      setSelectedRowKeys([]);
      fetchVouchers();
      fetchStatistics();
    } catch (error) {
      console.error('Error bulk updating vouchers:', error);
      message.error('Không thể cập nhật voucher');
    }
  };

  // Handle export
  const handleExport = async (format: 'csv' | 'json' | 'xlsx') => {
    try {
      await voucherService.exportVouchers(format, filters);
      message.success('Xuất dữ liệu thành công');
      // Download logic would go here
    } catch (error) {
      console.error('Error exporting vouchers:', error);
      message.error('Không thể xuất dữ liệu');
    }
  };

  // Get status color
  const getStatusColor = (status: VoucherStatus) => {
    switch (status) {
      case VoucherStatus.AVAILABLE: return 'green';
      case VoucherStatus.CLAIMED: return 'blue';
      case VoucherStatus.USED: return 'orange';
      case VoucherStatus.EXPIRED: return 'red';
      default: return 'default';
    }
  };

  // Get status text
  const getStatusText = (status: VoucherStatus) => {
    switch (status) {
      case VoucherStatus.AVAILABLE: return 'Có sẵn';
      case VoucherStatus.CLAIMED: return 'Đã nhận';
      case VoucherStatus.USED: return 'Đã sử dụng';
      case VoucherStatus.EXPIRED: return 'Đã hết hạn';
      default: return status;
    }
  };

  // Get type text
  const getTypeText = (type: VoucherType) => {
    switch (type) {
      case VoucherType.VOUCHER_10K: return '10K';
      case VoucherType.VOUCHER_20K: return '20K';
      case VoucherType.VOUCHER_50K: return '50K';
      case VoucherType.VOUCHER_100K: return '100K';
      case VoucherType.VOUCHER_200K: return '200K';
      case VoucherType.VOUCHER_300K: return '300K';
      case VoucherType.VOUCHER_500K: return '500K';
      case VoucherType.VOUCHER_1M: return '1M';
      case VoucherType.VOUCHER_2M: return '2M';
      default: return type;
    }
  };

  // Format value
  const formatValue = (value: number) => {
    return `${value.toLocaleString('vi-VN')} VNĐ`;
  };

  // Table columns
  const columns = [
    {
      title: 'Mã voucher',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      fixed: 'left' as const,
      render: (text: string) => <Text code style={{ fontSize: '12px' }}>{text}</Text>,
    },
    {
      title: 'Loại',
      dataIndex: 'voucher_type',
      key: 'voucher_type',
      width: 120,
      render: (type: VoucherType) => (
        <Tag color="blue">{getTypeText(type)}</Tag>
      ),
    },
    {
      title: 'Giá trị',
      dataIndex: 'value',
      key: 'value',
      width: 100,
      render: (value: number) => (
        <Text strong>{formatValue(value)}</Text>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: VoucherStatus) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Người dùng',
      key: 'user',
      width: 150,
      render: (record: Voucher) => {
        if (!record.user) {
          return <Text type="secondary">Chưa có</Text>;
        }
        return (
          <div>
            <div style={{ fontSize: '12px' }}>
              {record.user.name}
            </div>
            {record.user.email && (
              <div style={{ fontSize: '10px', color: '#666' }}>
                {record.user.email}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Ngày nhận',
      dataIndex: 'claimedAt',
      key: 'claimedAt',
      width: 120,
      render: (date: string | null) => {
        if (!date) return <Text type="secondary">Chưa nhận</Text>;
        return (
          <Text style={{ fontSize: '12px' }}>
            {dayjs(date).format('DD/MM/YYYY')}
          </Text>
        );
      },
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => (
        <Text style={{ fontSize: '12px' }}>
          {dayjs(date).format('DD/MM/YYYY')}
        </Text>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (record: Voucher) => (
        <Space size="small">
          <Tooltip title="Xem chi tiết">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewVoucher(record)}
            />
          </Tooltip>
          <Tooltip title="Chỉnh sửa">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditVoucher(record)}
            />
          </Tooltip>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'claim',
                  label: 'Đánh dấu đã nhận',
                  icon: <CheckOutlined />,
                  disabled: record.status === VoucherStatus.CLAIMED || record.status === VoucherStatus.USED,
                  onClick: () => handleStatusUpdate(record.id, VoucherStatus.CLAIMED),
                },
                {
                  key: 'use',
                  label: 'Đánh dấu đã dùng',
                  icon: <CheckOutlined />,
                  disabled: record.status !== VoucherStatus.CLAIMED,
                  onClick: () => handleStatusUpdate(record.id, VoucherStatus.USED),
                },
                {
                  key: 'expire',
                  label: 'Đánh dấu hết hạn',
                  icon: <CloseOutlined />,
                  disabled: record.status === VoucherStatus.EXPIRED,
                  onClick: () => handleStatusUpdate(record.id, VoucherStatus.EXPIRED),
                },
                {
                  type: 'divider',
                },
                {
                  key: 'delete',
                  label: 'Xóa',
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => handleDeleteVoucher(record),
                },
              ],
            }}
            trigger={['click']}
          >
            <Button type="text" size="small" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  // Row selection for bulk operations
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  return (
    <ErrorBoundary>
      <div style={{ padding: '24px' }}>
        {/* Statistics Cards */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Tổng voucher"
                value={summary.totalAvailable + summary.totalClaimed + summary.totalUsed + summary.totalExpired}
                prefix={<CalendarOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Đang hoạt động"
                value={summary.totalAvailable}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Đã sử dụng"
                value={summary.totalUsed}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Tổng giá trị"
                value={summary.totalValue}
                valueStyle={{ color: '#cf1322' }}
                suffix="VNĐ"
                formatter={(value) => `${Number(value).toLocaleString('vi-VN')}`}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters and Actions */}
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col span={6}>
              <Input.Search
                placeholder="Tìm kiếm mã voucher..."
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                onSearch={applyFilters}
                allowClear
              />
            </Col>
            <Col span={4}>
              <Select
                placeholder="Trạng thái"
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                allowClear
                style={{ width: '100%' }}
              >
                <Option value={VoucherStatus.AVAILABLE}>Có sẵn</Option>
                <Option value={VoucherStatus.CLAIMED}>Đã nhận</Option>
                <Option value={VoucherStatus.USED}>Đã sử dụng</Option>
                <Option value={VoucherStatus.EXPIRED}>Hết hạn</Option>
              </Select>
            </Col>
            <Col span={4}>
              <Select
                placeholder="Loại voucher"
                value={filters.voucher_type}
                onChange={(value) => handleFilterChange('voucher_type', value)}
                allowClear
                style={{ width: '100%' }}
              >
                <Option value={VoucherType.VOUCHER_10K}>10K</Option>
                <Option value={VoucherType.VOUCHER_20K}>20K</Option>
                <Option value={VoucherType.VOUCHER_50K}>50K</Option>
                <Option value={VoucherType.VOUCHER_100K}>100K</Option>
                <Option value={VoucherType.VOUCHER_200K}>200K</Option>
                <Option value={VoucherType.VOUCHER_300K}>300K</Option>
                <Option value={VoucherType.VOUCHER_500K}>500K</Option>
                <Option value={VoucherType.VOUCHER_1M}>1M</Option>
                <Option value={VoucherType.VOUCHER_2M}>2M</Option>
              </Select>
            </Col>
            <Col span={6}>
              <Space>
                <Button icon={<FilterOutlined />} onClick={applyFilters}>
                  Lọc
                </Button>
                <Button onClick={resetFilters}>Đặt lại</Button>
                <Button icon={<ReloadOutlined />} onClick={() => fetchVouchers()}>
                  Tải lại
                </Button>
              </Space>
            </Col>
            <Col span={4} style={{ textAlign: 'right' }}>
              <Space>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'csv',
                        label: 'Xuất CSV',
                        onClick: () => handleExport('csv'),
                      },
                      {
                        key: 'xlsx',
                        label: 'Xuất Excel',
                        onClick: () => handleExport('xlsx'),
                      },
                      {
                        key: 'json',
                        label: 'Xuất JSON',
                        onClick: () => handleExport('json'),
                      },
                    ],
                  }}
                  trigger={['click']}
                >
                  <Button icon={<DownloadOutlined />}>Xuất dữ liệu</Button>
                </Dropdown>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreateVoucher}
                >
                  Tạo voucher
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Bulk Actions */}
        {selectedRowKeys.length > 0 && (
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={16} align="middle">
              <Col>
                <Text>Đã chọn {selectedRowKeys.length} voucher</Text>
              </Col>
              <Col>
                <Space>
                  <Button
                    size="small"
                    onClick={() => handleBulkUpdate({ status: VoucherStatus.CLAIMED })}
                  >
                    Đánh dấu đã nhận
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleBulkUpdate({ status: VoucherStatus.EXPIRED })}
                  >
                    Đánh dấu hết hạn
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setSelectedRowKeys([])}
                  >
                    Bỏ chọn
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        )}

        {/* Vouchers Table */}
        <Card>
          <Table
            columns={columns}
            dataSource={vouchers}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} của ${total} voucher`,
            }}
            rowSelection={rowSelection}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
            size="small"
          />
        </Card>

        {/* Create/Edit Modal */}
        <Modal
          title={modalMode === 'create' ? 'Tạo voucher mới' : 'Chỉnh sửa voucher'}
          open={isModalVisible}
          onCancel={() => {
            setIsModalVisible(false);
            setSelectedVoucher(null);
          }}
          footer={null}
          width={800}
          destroyOnClose
        >
          <VoucherForm
            initialValues={selectedVoucher || undefined}
            onFinish={handleFormSubmit}
          />
        </Modal>

        {/* Detail Modal */}
        <VoucherDetail
          voucher={selectedVoucher}
          visible={isDetailVisible}
          onClose={() => {
            setIsDetailVisible(false);
            setSelectedVoucher(null);
          }}
          onEdit={handleEditVoucher}
          onDelete={handleDeleteVoucher}
        />

        {/* Delete Confirmation Modal */}
        <Modal
          title="Xác nhận xóa voucher"
          open={deleteModalVisible}
          onOk={confirmDeleteVoucher}
          onCancel={() => {
            setDeleteModalVisible(false);
            setVoucherToDelete(null);
          }}
          okText="Xóa"
          cancelText="Hủy"
          okButtonProps={{ danger: true }}
        >
          <p>
            Bạn có chắc chắn muốn xóa voucher{' '}
            <Text code>{voucherToDelete?.code}</Text>?
          </p>
          <p>Hành động này không thể hoàn tác.</p>
        </Modal>
      </div>
    </ErrorBoundary>
  );
};

export default VouchersPage;
